import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  dashApi, type BackupReadiness, type CoverageSummary, type PeriodCloseRow, type RetentionPolicy,
} from "@/lib/dashApi";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, Stat } from "@/components/dash/ui";

const TABS = [
  { key: "coverage", label: "Historical Coverage" },
  { key: "period", label: "Period Close" },
  { key: "backfill", label: "Backfill Readiness" },
  { key: "retention", label: "Retention" },
  { key: "backup", label: "Backup & Restore" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

export default function HistoricalDurabilityPage() {
  const location = useLocation();
  const [tab, setTab] = useState<TabKey>(
    location.pathname.endsWith("/period-closes") ? "period" : "coverage",
  );
  return (
    <div className="space-y-6">
      <PageHeader
        title="Historical Durability & Retention Governance"
        description="How much trustworthy history exists, which long-term comparisons are supported, what has been formally closed as an IRONDash reporting snapshot, and whether IRONDash is backed up. Missing history is shown honestly and never zero-filled; retention is dry-run only with deletion disabled by default; no backfill is initiated; no paths or secrets are exposed."
      />
      <div className="flex flex-wrap gap-2 text-sm">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`rounded px-3 py-1 ${tab === t.key ? "bg-sky-500/20 text-sky-200" : "bg-slate-800 text-slate-400"}`}>{t.label}</button>
        ))}
      </div>
      {tab === "coverage" && <CoverageTab />}
      {tab === "period" && <PeriodCloseTab />}
      {tab === "backfill" && <BackfillTab />}
      {tab === "retention" && <RetentionTab />}
      {tab === "backup" && <BackupTab />}
    </div>
  );
}

const STATUS_TONE: Record<string, string> = {
  complete: "text-emerald-300", introduced_late: "text-amber-300", recent_only: "text-amber-300",
  partial: "text-amber-300", gapped: "text-rose-300", insufficient_history: "text-rose-300",
  unknown: "text-slate-500", not_applicable: "text-slate-500",
};
const READY_TONE: Record<string, string> = {
  history_adequate: "text-emerald-300", history_partial: "text-amber-300",
  comparison_window_incomplete: "text-rose-300", history_insufficient: "text-rose-300",
};

function CoverageTab() {
  const q = useQuery({ queryKey: ["hist-coverage"], queryFn: dashApi.historyCoverageSummary });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  const c = q.data as CoverageSummary;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="History begins" value={c.global_earliest ?? "—"} />
        <Stat label="Adequate" value={c.counts.adequate} />
        <Stat label="Partial" value={c.counts.partial} />
        <Stat label="Gaps / unknown" value={c.counts.gaps} />
        <Stat label="Connectors failing" value={c.counts.connectors_failing} sub={c.connectors_failing.join(", ") || "none"} />
      </div>
      <Panel title="Long-term comparison capability" subtitle="Available only where the equal-length prior window genuinely exists — never zero-filled.">
        <div className="grid grid-cols-3 gap-2 text-xs">
          {(["mom_ready_domains", "qoq_ready_domains", "yoy_ready_domains"] as const).map((k) => (
            <div key={k} className="rounded border border-slate-800 bg-slate-900/40 p-2">
              <div className="text-[10px] uppercase text-slate-500">{k.replace("_ready_domains", "").toUpperCase()}-ready</div>
              <div className="text-slate-200">{c.comparison_capability[k].length ? c.comparison_capability[k].join(", ") : <span className="text-rose-300">none (insufficient history)</span>}</div>
            </div>
          ))}
        </div>
      </Panel>
      <Panel title="Coverage by source">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-slate-500">
              <th className="pb-1 pr-3">Source</th><th className="pb-1 pr-3">Status</th><th className="pb-1 pr-3">Connector</th>
              <th className="pb-1 pr-3">Range</th><th className="pb-1 pr-3">Continuity</th><th className="pb-1">Notes</th>
            </tr></thead>
            <tbody>
              {c.sources.map((s) => (
                <tr key={s.source} className="border-t border-slate-800 align-top">
                  <td className="py-1.5 pr-3 text-slate-200">{s.label}</td>
                  <td className={`py-1.5 pr-3 ${STATUS_TONE[s.status] ?? "text-slate-400"}`}>{s.status}</td>
                  <td className={`py-1.5 pr-3 ${s.connector_failing ? "text-rose-300" : "text-slate-400"}`}>{s.connector_status}</td>
                  <td className="py-1.5 pr-3 text-slate-500">{s.earliest_date ?? "—"} → {s.latest_date ?? "—"}</td>
                  <td className="py-1.5 pr-3 text-slate-400">{s.continuity_pct != null ? `${s.continuity_pct}%` : "—"}</td>
                  <td className="py-1.5 text-[10px] text-slate-500">{s.caveats.map((cv, i) => <div key={i}>• {cv}</div>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <Panel title="Comparison readiness by domain">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-slate-500"><th className="pb-1 pr-3">Domain</th><th className="pb-1 pr-3">MTD</th><th className="pb-1 pr-3">MoM</th><th className="pb-1 pr-3">QoQ</th><th className="pb-1">YoY</th></tr></thead>
            <tbody>
              {Object.entries(c.readiness).map(([k, r]) => (
                <tr key={k} className="border-t border-slate-800">
                  <td className="py-1 pr-3 text-slate-200">{r.label}</td>
                  {([r.mtd, r.mom, r.qoq, r.yoy]).map((w, i) => (
                    <td key={i} className={`py-1 pr-3 ${READY_TONE[w.label] ?? "text-slate-400"}`}>{w.label.replace("history_", "").replace("comparison_window_", "")}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <p className="text-[11px] text-slate-600">{c.no_fabrication}</p>
    </div>
  );
}

function PeriodCloseTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["period-closes"], queryFn: () => dashApi.periodCloses() });
  const [ctype, setCtype] = useState("monthly");
  const [ref, setRef] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const inv = () => qc.invalidateQueries({ queryKey: ["period-closes"] });
  const create = useMutation({
    mutationFn: () => dashApi.createPeriodClose({ close_type: ctype, ref_date: ref }),
    onSuccess: () => { setMsg("Draft created."); inv(); },
    onError: (e) => setMsg(e instanceof Error ? e.message : "failed"),
  });
  return (
    <div className="space-y-5">
      <Panel title="Create a period-close draft" subtitle="An IRONDash reporting snapshot — NOT an accounting or legal close. Preview/finalise never change any source, target, decision or action.">
        <div className="flex flex-wrap items-end gap-2">
          <select value={ctype} onChange={(e) => setCtype(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-slate-200">
            {["monthly", "quarterly", "annual"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="date" value={ref} onChange={(e) => setRef(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-slate-200" />
          <button onClick={() => ref && create.mutate()} disabled={create.isPending || !ref} className="rounded bg-sky-500/20 px-3 py-1.5 text-sm text-sky-200 disabled:opacity-40">Create draft</button>
          {msg && <span className="text-xs text-slate-400">{msg}</span>}
        </div>
        <p className="mt-2 text-[11px] text-amber-300/80">Pick a date inside the period you want to close (e.g. the last day of the month).</p>
      </Panel>
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : (
        (q.data?.closes.length ?? 0) === 0 ? <EmptyState>No period closes yet.</EmptyState>
          : <div className="space-y-2">{q.data!.closes.map((c) => <CloseRow key={c.id} c={c} onChange={inv} />)}</div>
      )}
    </div>
  );
}

const CLOSE_TONE: Record<string, string> = { draft: "text-slate-400", finalised: "text-emerald-300", superseded: "text-amber-300", voided: "text-rose-300", previewed: "text-slate-400" };

function CloseRow({ c, onChange }: { c: PeriodCloseRow; onChange: () => void }) {
  const [err, setErr] = useState<string | null>(null);
  const run = (p: Promise<unknown>) => p.then(() => { setErr(null); onChange(); }).catch((e) => setErr(e instanceof Error ? e.message : "failed"));
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/30 p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded bg-slate-800 px-1.5 py-0.5 text-[10px] ${CLOSE_TONE[c.status]}`}>{c.status}</span>
        <span className="text-slate-200">{c.title}</span>
        <span className="text-slate-500">{c.period_start} → {c.period_end}</span>
        {c.content_hash && <span className="text-[10px] text-slate-600" title={c.content_hash}>#{c.content_hash.slice(0, 10)}</span>}
        {c.immutable && <span className="text-[10px] text-emerald-400">immutable</span>}
        <div className="ml-auto flex gap-2">
          {["draft", "previewed"].includes(c.status) && (
            <button onClick={() => run(dashApi.finalisePeriodClose(c.id))} className="rounded bg-emerald-500/15 px-2 py-0.5 text-emerald-300">finalise</button>)}
          {c.status === "finalised" && (
            <button onClick={() => { const n = prompt("Supersede note (creates a revision):"); if (n) run(dashApi.supersedePeriodClose(c.id, n)); }} className="rounded bg-slate-700 px-2 py-0.5 text-slate-300">supersede</button>)}
          {c.status !== "voided" && c.status !== "superseded" && (
            <button onClick={() => { const r = prompt("Void reason:"); if (r) run(dashApi.voidPeriodClose(c.id, r)); }} className="rounded bg-rose-500/15 px-2 py-0.5 text-rose-300">void</button>)}
        </div>
      </div>
      {c.void_reason && <p className="mt-1 text-rose-300">voided: {c.void_reason}</p>}
      {c.parent_close_id && <p className="mt-1 text-slate-500">revision of #{c.parent_close_id}</p>}
      {err && <p className="mt-1 text-rose-300">{err}</p>}
    </div>
  );
}

const PLAN_TONE: Record<string, string> = {
  draft: "text-slate-400", assessed: "text-sky-300", awaiting_approval: "text-amber-300",
  approved_for_pilot: "text-emerald-300", blocked: "text-rose-300", cancelled: "text-slate-500", superseded: "text-slate-500",
};

function BackfillPlansSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["backfill-plans"], queryFn: dashApi.backfillPlans });
  const [src, setSrc] = useState("odoo");
  const [msg, setMsg] = useState<string | null>(null);
  const inv = () => qc.invalidateQueries({ queryKey: ["backfill-plans"] });
  const create = useMutation({ mutationFn: () => dashApi.createBackfillPlan({ source_system: src }), onSuccess: inv });
  const act = (id: number, action: string, body?: Record<string, unknown>) =>
    dashApi.backfillPlanAction(id, action, body).then(() => { setMsg(null); inv(); }).catch((e) => setMsg(e instanceof Error ? e.message : "failed"));
  return (
    <Panel title="Backfill plans (approval governance)" subtitle="Prepares a future backfill. Nothing here executes an import — approval only authorises a later, separately-run pilot.">
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <select value={src} onChange={(e) => setSrc(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200">
          {["odoo", "prestashop", "repairs", "payments", "chat", "pim", "rota"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => create.mutate()} className="rounded bg-sky-500/20 px-3 py-1 text-xs text-sky-200">new draft plan</button>
        {msg && <span className="text-[11px] text-rose-300">{msg}</span>}
      </div>
      {q.isLoading ? <LoadingState /> : (q.data?.plans.length ?? 0) === 0 ? <EmptyState>No backfill plans.</EmptyState> : (
        <div className="space-y-1 text-xs">
          {q.data!.plans.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-2 border-t border-slate-800 py-1.5">
              <span className={`rounded bg-slate-800 px-1.5 py-0.5 text-[10px] ${PLAN_TONE[p.status] ?? "text-slate-400"}`}>{p.status}</span>
              <span className="text-slate-200">#{p.id} {p.title || p.source_system}</span>
              <span className="text-slate-500">{p.source_date_start ?? "?"}→{p.source_date_end ?? "?"}</span>
              <span className="text-[10px] text-slate-600">not executed</span>
              <div className="ml-auto flex gap-1">
                {["draft", "blocked"].includes(p.status) && <button onClick={() => act(p.id, "assess")} className="rounded bg-slate-700 px-2 py-0.5 text-slate-300">assess</button>}
                {["draft", "assessed", "blocked"].includes(p.status) && <button onClick={() => act(p.id, "request-approval")} className="rounded bg-slate-700 px-2 py-0.5 text-slate-300">request approval</button>}
                {p.status === "awaiting_approval" && <button onClick={() => { const a = prompt("Named approver:"); if (a) act(p.id, "approve-pilot", { approver: a }); }} className="rounded bg-emerald-500/15 px-2 py-0.5 text-emerald-300">approve pilot</button>}
                {p.status !== "cancelled" && p.status !== "superseded" && <button onClick={() => { const r = prompt("Cancel reason:"); if (r) act(p.id, "cancel", { reason: r }); }} className="rounded bg-rose-500/15 px-2 py-0.5 text-rose-300">cancel</button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-2 text-[11px] text-slate-600">{q.data?.note}</p>
    </Panel>
  );
}

function BackfillTab() {
  const q = useQuery({ queryKey: ["backfill"], queryFn: dashApi.backfillReadiness });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-200/80">{q.data!.note}</div>
      <BackfillPlansSection />
      {q.data!.assessments.map((a) => (
        <Panel key={a.id} title={a.label} subtitle={`state: ${a.state} · backfill possible: ${a.backfill_possible} · approval required: ${a.approval_required ? "yes" : "no"}`}>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
            <div><dt className="text-slate-500">Warehouse earliest</dt><dd className="text-slate-300">{a.earliest_warehouse_date ?? "—"}</dd></div>
            <div><dt className="text-slate-500">Source earliest</dt><dd className="text-slate-300">{a.earliest_source_date ?? "not assessed"}</dd></div>
            <div><dt className="text-slate-500">Load impact</dt><dd className="text-slate-300">{a.load_impact ?? "—"}</dd></div>
          </dl>
          {a.safe_query_method && <p className="mt-1 text-[11px] text-slate-500">Method: {a.safe_query_method}</p>}
          {a.known_limitations && <p className="mt-1 text-[11px] text-slate-500">Limitations: {a.known_limitations}</p>}
        </Panel>
      ))}
    </div>
  );
}

function RetentionTab() {
  const q = useQuery({ queryKey: ["retention"], queryFn: dashApi.retention });
  const [dry, setDry] = useState<Record<string, string>>({});
  const dryRun = useMutation({
    mutationFn: (domain: string) => dashApi.retentionDryRun(domain),
    onSuccess: (d) => setDry((s) => ({ ...s, [d.domain]: `${d.eligible_count}/${d.total_count} eligible — ${d.note}` })),
  });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  const d = q.data!;
  return (
    <div className="space-y-4">
      <div className={`rounded-lg border p-3 text-[11px] ${d.any_deletion_enabled ? "border-rose-500/40 bg-rose-500/5 text-rose-200" : "border-emerald-500/30 bg-emerald-500/5 text-emerald-200/80"}`}>
        {d.any_deletion_enabled ? "⚠ A non-protected domain has deletion enabled." : "✓ No automated deletion is enabled. All policies are monitoring + dry-run only."} {d.note}
      </div>
      <Panel title="Retention policies">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-slate-500"><th className="pb-1 pr-3">Domain</th><th className="pb-1 pr-3">Class</th><th className="pb-1 pr-3">Days</th><th className="pb-1 pr-3">Deletion</th><th className="pb-1 pr-3">Dry-run</th><th className="pb-1">Action</th></tr></thead>
            <tbody>
              {d.policies.map((p: RetentionPolicy) => (
                <tr key={p.id} className="border-t border-slate-800 align-top">
                  <td className="py-1.5 pr-3 text-slate-200">{p.label}{p.protected && <span className="ml-1 text-[10px] text-emerald-400">protected</span>}</td>
                  <td className="py-1.5 pr-3 text-slate-500">{p.retention_class}</td>
                  <td className="py-1.5 pr-3 text-slate-400">{p.retention_days ?? "—"}</td>
                  <td className={`py-1.5 pr-3 ${p.deletion_enabled ? "text-rose-300" : "text-emerald-300"}`}>{p.deletion_enabled ? "enabled" : "disabled"}</td>
                  <td className="py-1.5 pr-3 text-slate-400">{p.dry_run ? "on" : "off"}</td>
                  <td className="py-1.5">
                    <button onClick={() => dryRun.mutate(p.domain)} className="rounded bg-slate-700 px-2 py-0.5 text-slate-300">dry-run</button>
                    {dry[p.domain] && <div className="mt-1 text-[10px] text-slate-500">{dry[p.domain]}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function BackupTab() {
  const q = useQuery({ queryKey: ["restore-readiness"], queryFn: dashApi.restoreReadiness });
  const [plan, setPlan] = useState<string[] | null>(null);
  const preview = useMutation({ mutationFn: dashApi.restoreDrillPreview, onSuccess: (d) => setPlan(d.plan) });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  const b = q.data!.backup as BackupReadiness;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Last backup" value={b.last_backup_at ? b.last_backup_at.slice(0, 16).replace("T", " ") : "—"} sub={b.fresh ? "fresh" : "stale / unknown"} />
        <Stat label="Location" value={b.location_class} />
        <Stat label="Backups kept" value={b.retention_count} />
        <Stat label="Schema" value={b.live_schema_version ?? "—"} />
      </div>
      <Panel title="Backup readiness" subtitle={b.safety}>
        <ul className="space-y-1 text-xs text-slate-400">
          {b.caveats.map((c, i) => <li key={i}>• {c}</li>)}
        </ul>
      </Panel>
      <Panel title="Restore drill" subtitle="Never restores over production; isolated only.">
        <button onClick={() => preview.mutate()} className="rounded bg-slate-700 px-3 py-1.5 text-xs text-slate-300">preview drill plan</button>
        {plan && (
          <ol className="mt-2 list-decimal space-y-0.5 pl-5 text-xs text-slate-400">
            {plan.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        )}
        {q.data!.drills.length > 0 && (
          <div className="mt-3 text-xs">
            <p className="text-slate-500">Recent drills</p>
            {q.data!.drills.map((dr) => (
              <div key={dr.id} className="mt-1 text-slate-400"><span className={dr.status === "passed" ? "text-emerald-300" : "text-amber-300"}>{dr.status}</span> — {dr.result_summary || dr.blocked_reason}</div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
